import { PickerColumnItem } from 'antd-mobile/es/components/picker-view';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SelectPicker } from '../../../../components/common/inputs/SelectPicker';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import { SurveyNakedInput } from '../../../../components/common/survey/inputs/SurveyNakedInput';
import { SurveyOverviewNavigationList } from '../../../../components/common/survey/inputs/SurveyOverviewNavigationList';
import { SearchModelModal } from '../../../../components/custom/modal/toast/SearchModelModal';
import { MACHINE_ROW_OPTIONS, MachineApprovalStatusType } from '../../../../constants/machine/machine.constant';
import { sellTractorPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { sellTractorSurveySteps } from '../../../../constants/survey/sellTractorSurvey';
import { useGetBrandsQuery } from '../../../../hooks/api/brand/queries/useGetBrands.query';
import { useGetMachinesQuery } from '../../../../hooks/api/machines/queries/useGetMachines.query';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { useGetUserProfile } from '../../../../hooks/user/useGetUserProfile';
import { MachineType } from '../../../../interfaces/Brand.interfaces';
import { IMachineListMachine } from '../../../../interfaces/Dictionary.interfaces';
import { sellTractorMachineInfo, userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { SellTractorSurveyPageWrapper } from '../SellTractorSurveyPageWrapper';

const ERROR_MESSAGES = {
  brand: {
    title: '브랜드를 선택해 주세요',
    description: '',
  },
  model: {
    title: '모델을 선택해 주세요',
    description: '',
  },
  engineOutputInPs: {
    title: '마력을 입력해 주세요',
    description: '',
  },
  machineRow: {
    title: '조식을 선택해 주세요',
    description: '',
  },
  manufacturedYear: {
    title: '매물 연식을 선택해 주세요',
    description: '',
  },
  operationTime: {
    title: '사용 시간을 입력해 주세요',
    description: '',
    invalidFormat: {
      title: '❌ 숫자를 입력 해주세요',
      description: '문자와 특수문자는 입력이 불가능해요!\n소수점 입력이 필요한 경우\n반올림 해주세요!',
    },
  },
  location: {
    title: '판매 지역을 입력해 주세요',
    description: '',
  },
} as const;

export const SellTractorSurveyRequiredInputOverviewPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { userProfile } = useGetUserProfile();
  const userSellTractorSurveySelector = useAppSelector((state) => state.userSellTractorSurvey);
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;

  const [selectedBrand, setSelectedBrand] = useState<PickerColumnItem>();
  const [manufacturedYear, setManufacturedYear] = useState<PickerColumnItem>();
  const [machineRow, setMachineRow] = useState<PickerColumnItem>();

  const [model, setModel] = useState<string>();
  const [selectedMachine, setSelectedMachine] = useState<sellTractorMachineInfo>();
  const [machineSearchTerm, setMachineSearchTerm] = useState<string>();
  const [engineOutputInPs, setEngineOutputInPs] = useState<number>();
  const [operationTime, setOperationTime] = useState<number>();

  const [isBrandsPickerVisible, setIsBrandsPickerVisible] = useState<boolean>(false);
  const [isModelSearchModalVisible, setIsModelSearchModalVisible] = useState<boolean>(false);
  const [isManufacturedYearPickerVisible, setIsManufacturedYearPickerVisible] = useState<boolean>(false);
  const [isMachineRowPickerVisible, setIsMachineRowPickerVisible] = useState<boolean>(false);

  const [isShowDisabledInputModal, setIsShowDisabledInputModal] = useState<boolean>(false);
  const [isShowErrorModal, setIsShowErrorModal] = useState<boolean>(false);

  const [inputError, setInputError] = useState<{ title: string; description: string; errorHandler?: () => void }>();

  const [isEngineOutputInPsFocus, setIsEngineOutputInPsFocus] = useState<boolean>(false);
  const [isOperationTimeFocus, setIsOperationTimeFocus] = useState<boolean>(false);
  const selectedMachineType = (userSellTractorSurveySelector.machineType as MachineType) ?? 'tractor';

  const { data: brands, isLoading: isGetBrandsQueryLoading } = useGetBrandsQuery({
    searchParam: { applicableMachineType: selectedMachineType },
    options: { enabled: !!selectedMachineType },
  });

  const { data: machines } = useGetMachinesQuery({
    searchParam: {
      machineType: selectedMachineType,
      brandId: selectedBrand?.value as string,
      fields: 'id,model,engineOutputInPs,approvalStatus,rowConfig',
      searchTerm: machineSearchTerm ?? '',
    },
    options: { enabled: !!selectedBrand && !!selectedMachineType, cacheTime: 1000 * 60 * 5 },
  });

  const transformedBrandsForPicker = useMemo<{ label: string; value: string }[][]>(() => {
    if (brands) {
      const transformedBrands = brands.map((brand) => {
        return {
          label: brand.name,
          value: brand.id,
        };
      });

      return [transformedBrands];
    }

    return [];
  }, [brands]);

  const transformedManufacturedYearsForPicker = [
    [...Array(new Date().getFullYear() - 1989)].map((_, i) => ({
      label: `${1990 + i}`,
      value: `${1990 + i}`,
    })),
  ];

  const navigateToLocationInput = () => {
    navigate(sellTractorPageInfo.locationPage.path, { replace: true });
  };

  const currentStep = sellTractorSurveySteps.STEP1;
  const steps = Object.keys(sellTractorSurveySteps);
  const registrationSteps = steps.slice(0, 3);
  const isRegistration = !userSellTractorSurveySelector.sellTractorIdForEdit;

  const isOwnTractor = !!userSellTractorSurveySelector.userOwnTractorId;

  const isTractorRequiredInputEmpty = useMemo(() => {
    return selectedMachineType === 'tractor' && (!selectedBrand || !model || !engineOutputInPs);
  }, [selectedMachineType, selectedBrand, model, engineOutputInPs]);

  const renderMachineSpecificInput = useCallback(
    (machineType: MachineType) => {
      const commonProps = {
        isFocus: isEngineOutputInPsFocus,
      };

      if (machineType === 'tractor') {
        return (
          <SurveyNakedInput
            {...commonProps}
            type={'number'}
            label={'마력'}
            placeholder={'입력하기'}
            inputValue={engineOutputInPs ?? ''}
            onClickHandler={() => {
              if (!isRegistration || isOwnTractor) {
                setIsShowDisabledInputModal(true);
              }
            }}
            isEditable={isRegistration && (!selectedMachine?.id || !selectedMachine?.engineOutputInPs)}
            onChange={(value: string | number | undefined) => setEngineOutputInPs(value as number | undefined)}
          />
        );
      }

      if (machineType === 'combine') {
        return (
          <SurveyNakedInput
            {...commonProps}
            type={'text'}
            label={'조식'}
            placeholder={'선택하기'}
            inputValue={machineRow?.value ?? ''}
            onClickHandler={() => {
              if (!isRegistration) {
                setIsShowDisabledInputModal(true);
                return;
              }

              if (isRegistration && !selectedMachine?.id) {
                setIsMachineRowPickerVisible(true);
              } else {
                setIsMachineRowPickerVisible(false);
              }
            }}
            isEditable={isRegistration && !selectedMachine?.id}
            isButton
          />
        );
      }

      return null;
    },
    [
      engineOutputInPs,
      machineRow?.value,
      isRegistration,
      isEngineOutputInPsFocus,
      userSellTractorSurveySelector.sellTractorIdForEdit,
      userSellTractorSurveySelector.userOwnTractorId,
    ],
  );

  useEffect(() => {
    const {
      brand,
      brandId,
      operationTime,
      manufacturedYear,
      model,
      engineOutputInPs,
      sellTractorMachineInfo,
      rowConfig,
    } = userSellTractorSurveySelector;

    if (brand) {
      setSelectedBrand({ label: brand, value: brandId as string });
    }
    setModel(model);
    setSelectedMachine(sellTractorMachineInfo);
    setEngineOutputInPs(engineOutputInPs);
    setOperationTime(operationTime);
    setManufacturedYear({ label: manufacturedYear, value: manufacturedYear as number });
    setMachineRow({ label: rowConfig, value: rowConfig as string });
  }, [userSellTractorSurveySelector]);

  useEffect(() => {
    const { regionDepth1, regionDepth2, regionDepth3 } = userProfile || {};

    if (!userSellTractorSurveySelector.location && regionDepth1 && regionDepth2 && regionDepth3) {
      const location = `${regionDepth1} ${regionDepth2} ${regionDepth3}`;
      dispatch(userSellTractorSurveyActions.changeLocation(location));
    }
  }, [userProfile, userSellTractorSurveySelector.location]);

  const dispatchSellTractorInputValuesToStore = () =>
    dispatch(
      userSellTractorSurveyActions.changeSellTractorRequiredInputs({
        machineType: selectedMachineType,
        brand: selectedBrand?.label as string,
        brandId: selectedBrand?.value as string,
        model: model as string,
        engineOutputInPs: engineOutputInPs as number,
        sellTractorMachineInfo: {
          id: selectedMachine?.id as string,
          approvalStatus: selectedMachine?.approvalStatus as MachineApprovalStatusType,
          engineOutputInPs: selectedMachine?.engineOutputInPs as number,
        },
        rowConfig: machineRow?.value as string,
        manufacturedYear: manufacturedYear?.value as number,
        operationTime: operationTime as number,
      }),
    );

  const getInputError = () => {
    if (!selectedBrand) {
      return {
        ...ERROR_MESSAGES.brand,
        errorHandler: () => setIsBrandsPickerVisible(true),
      };
    }

    if (!model) {
      return {
        ...ERROR_MESSAGES.model,
        errorHandler: () => setIsModelSearchModalVisible(true),
      };
    }

    if (selectedMachineType === 'combine' && !machineRow?.value) {
      return {
        ...ERROR_MESSAGES.machineRow,
        errorHandler: () => setIsMachineRowPickerVisible(true),
      };
    }

    if (selectedMachineType === 'tractor' && !engineOutputInPs) {
      setIsEngineOutputInPsFocus(false);
      return {
        ...ERROR_MESSAGES.engineOutputInPs,
        errorHandler: () => setIsEngineOutputInPsFocus(true),
      };
    }

    if (!manufacturedYear?.value) {
      return {
        ...ERROR_MESSAGES.manufacturedYear,
        errorHandler: () => setIsManufacturedYearPickerVisible(true),
      };
    }

    if (!operationTime || !/^[0-9]+$/.test(operationTime.toString())) {
      setIsOperationTimeFocus(false);
      return {
        ...(operationTime ? ERROR_MESSAGES.operationTime.invalidFormat : ERROR_MESSAGES.operationTime),
        errorHandler: () => setIsOperationTimeFocus(true),
      };
    }

    if (!userSellTractorSurveySelector.location) {
      return ERROR_MESSAGES.location;
    }

    return null;
  };

  return (
    <>
      <SellTractorSurveyPageWrapper
        headerTitle={'판매하실 매물의\n필수 정보를 입력해주세요'}
        canConfirm
        steps={isRegistration ? registrationSteps : steps}
        currentStep={currentStep}
        confirmHandler={() => {
          const validationError = getInputError();
          if (validationError) {
            setInputError(validationError);
            setIsShowErrorModal(true);
            return;
          }
          dispatchSellTractorInputValuesToStore();
          let nextPath = sellTractorPageInfo.optionPage.path;
          if (isRegistration) {
            nextPath = sellTractorPageInfo.pricePage.path;
          }
          navigate(nextPath, { replace: true });
        }}
        closeHandler={dispatchSellTractorInputValuesToStore}
        isTractorRequiredInputEmpty={isTractorRequiredInputEmpty}
      >
        <SurveyForm padding={'0px 16px'}>
          <SurveyInputHeader inputTitle={'매물 필수 정보'} isRequired />
          <SurveyOverviewNavigationList>
            <SurveyNakedInput
              type={'text'}
              label={'브랜드'}
              placeholder={'선택하기'}
              inputValue={selectedBrand?.label as string}
              onClickHandler={() => {
                if (isRegistration && !isOwnTractor) {
                  setIsBrandsPickerVisible(true);
                } else {
                  setIsShowDisabledInputModal(true);
                }
              }}
              isEditable={isRegistration && !isOwnTractor}
              isButton
            />
            <SurveyNakedInput
              type={'text'}
              label={'모델'}
              placeholder={'선택하기'}
              inputValue={model ?? ''}
              onClickHandler={() => {
                if (isRegistration && !isOwnTractor) {
                  setIsModelSearchModalVisible(true);
                } else {
                  setIsShowDisabledInputModal(true);
                }
              }}
              isEditable={isRegistration && !isOwnTractor}
              isButton
            />
            {renderMachineSpecificInput(selectedMachineType)}
            <SurveyNakedInput
              type={'number'}
              label={'연식'}
              placeholder={'선택하기'}
              inputValue={manufacturedYear?.value ?? ''}
              onClickHandler={() => setIsManufacturedYearPickerVisible(true)}
              isButton
            />
            <SurveyNakedInput
              type={'number'}
              label={'사용시간'}
              placeholder={'입력하기'}
              inputValue={operationTime ?? ''}
              onChange={(value: string | number | undefined) => setOperationTime(value as number | undefined)}
              isFocus={isOperationTimeFocus}
            />
            <SurveyNakedInput
              type={'text'}
              label={'판매 지역'}
              placeholder={'주소 찾기'}
              inputValue={userSellTractorSurveySelector.location ?? ''}
              onClickHandler={() => {
                dispatchSellTractorInputValuesToStore();
                navigateToLocationInput();
              }}
              isButton
            />
          </SurveyOverviewNavigationList>
        </SurveyForm>
      </SellTractorSurveyPageWrapper>

      <SelectPicker
        columns={transformedBrandsForPicker}
        visible={isBrandsPickerVisible}
        onConfirm={(selectedBrandName, extendBrandValue) => {
          setSelectedBrand(extendBrandValue.items[0] as PickerColumnItem);
          setMachineSearchTerm('');
          setModel('');
          setEngineOutputInPs(undefined);
          setSelectedMachine(undefined);
          setMachineRow(undefined);
        }}
        onClose={() => setIsBrandsPickerVisible(false)}
      />

      <SelectPicker
        columns={transformedManufacturedYearsForPicker}
        visible={isManufacturedYearPickerVisible}
        defaultValue={'2020'}
        onConfirm={(selectedYear, extendYearValue) => {
          setManufacturedYear(extendYearValue.items[0] as PickerColumnItem);
        }}
        onClose={() => setIsManufacturedYearPickerVisible(false)}
      />

      <SelectPicker
        columns={[MACHINE_ROW_OPTIONS]}
        visible={isMachineRowPickerVisible}
        defaultValue={'2조식'}
        onConfirm={(selectedRow, extendRowValue) => {
          setMachineRow(extendRowValue.items[0] as PickerColumnItem);
        }}
        onClose={() => setIsMachineRowPickerVisible(false)}
      />

      <SearchModelModal
        isSlideModalOpen={isModelSearchModalVisible}
        closeAll={() => setIsModelSearchModalVisible(false)}
        machines={machines}
        changeMachine={(newMachine: IMachineListMachine) => {
          setModel(newMachine.model);
          setMachineSearchTerm(newMachine.model);
          setSelectedMachine({
            id: newMachine.id,
            approvalStatus: newMachine.approvalStatus,
            engineOutputInPs: newMachine.engineOutputInPs,
          });
          if (selectedMachineType === 'combine') {
            setMachineRow({ label: newMachine.rowConfig, value: newMachine.rowConfig as string });
            return;
          }
          setEngineOutputInPs(newMachine.engineOutputInPs);
        }}
        model={model}
        machineSearchTerm={machineSearchTerm}
        changeMachineSearchTerm={(newSearchTerm: string) => setMachineSearchTerm(newSearchTerm)}
      />

      <PopupConfirmModal
        isShow={isShowDisabledInputModal}
        hasCloseButton={false}
        closeModal={() => setIsShowDisabledInputModal(false)}
        title={'이 정보를 바꾸시려면\n새로 신청해야해요!'}
        description={'매물의 브랜드, 모델, 마력 정보는\n수정이 불가능합니다.\n메인 화면에서 새로 신청해 주세요!'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => setIsShowDisabledInputModal(false),
            color: 'blue',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isShowErrorModal}
        hasCloseButton={false}
        closeModal={() => setIsShowErrorModal(false)}
        title={inputError?.title ?? ''}
        description={inputError?.description ?? ''}
        buttonArray={[
          {
            text: '확인',
            onClick: () => {
              setIsShowErrorModal(false);
              inputError?.errorHandler?.();
            },
            color: 'blue',
          },
        ]}
      />
    </>
  );
};
